import { Box, Button, Center, Flex, Text } from '@chakra-ui/react';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { DeviceSystem } from 'enums/shared/deviceSystem';
import { Form, Formik } from 'formik';
import useVerifyOtp from 'hooks/modules/otp/useVerifyOtp';
import useAppDispatch from 'hooks/shared/store/useAppDispatch';
import useFormError from 'hooks/shared/useFormError';
import useToastError from 'hooks/shared/useToastError';
import { OtpId } from 'model/modules/otp/types';
import { FC, useState } from 'react';
import Countdown from 'react-countdown';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumber } from 'react-phone-number-input';
import {
  useCheckOtpMutation,
  useCheckPhoneMutation,
} from 'rtkQuery/sharespro/queries/login';
import { authSlice } from 'slices/modules/auth/authSlice';
import * as Yup from 'yup';

import { ButtonVariant } from 'presentation/theme/components/button';
import LoginTemplate from 'presentation/components/modules/login/LoginTemplate';
import FormNumberInput from 'presentation/components/shared/form/FormNumberInput';
import { Color } from 'presentation/theme/colorScheme';
import ReCAPTCHA from 'react-google-recaptcha';
import { isSharesproFetchQueryError } from 'rtkQuery/sharespro/isSharesproFetchQueryError';
import { SharesproApiResponse } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import { SharesproApiErrorCode } from 'enums/shared/sharesproApiErrorCode';
import { useUpdateAgreementsMutation } from 'rtkQuery/sharespro/queries/profile';
import { getOrCreateAttributionVisitorId } from 'utils/attribution';

interface Props {
  goBack: () => void;
  phoneNumber: string | null;
  otpId: OtpId;
}

interface FormValues {
  otp: string;
}

const LoginConfirmPhoneContainer: FC<Props> = ({
  goBack,
  phoneNumber,
  otpId,
}) => {
  const { t } = useTranslation();

  const { lastOtpId, sendNewCodeDate, updateOtpId } = useVerifyOtp<OtpId>({
    initialOtpId: otpId,
  });

  const [checkOtp, { isLoading: otpVerifying }] = useCheckOtpMutation();
  const [checkPhone, { isLoading: newCodeRequesting }] =
    useCheckPhoneMutation();
  const [updateAgreements] = useUpdateAgreementsMutation();

  const handleFormError = useFormError<FormValues>();
  const handleToastError = useToastError();

  const [showCaptcha, setShowCaptcha] = useState(false);

  const dispatch = useAppDispatch();

  const onSendNewCode = (captchaToken?: string | null): void => {
    if (!phoneNumber) return;
    FingerprintJS.load()
      .then(async (fp) => await fp.get())
      .then(({ visitorId }) => {
        const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
        if (parsedPhoneNumber) {
          checkPhone({
            phone: parsedPhoneNumber.number,
            app_id: visitorId,
            device_system: DeviceSystem.Web,
            visitor_id: getOrCreateAttributionVisitorId(),
            captcha_token: captchaToken,
          })
            .unwrap()
            .then((otpId) => {
              updateOtpId(otpId);
            })
            .catch((err) => {
              if (
                isSharesproFetchQueryError(err) &&
                (err.data as SharesproApiResponse<any>).error_code ===
                  SharesproApiErrorCode.CaptchaRequired
              ) {
                setShowCaptcha(true);
              } else {
                handleToastError(err);
              }
            });
        }
      });
  };

  const validationSchema = Yup.object().shape({
    otp: Yup.string().required(t('fieldIsRequired') ?? undefined),
  });

  return (
    <LoginTemplate title={t('authorization')} hint={t('enterCodeFromSms')}>
      <Box mb="20px">
        <Formik<FormValues>
          initialValues={{
            otp: '',
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, formik) => {
            try {
              const sid = await checkOtp({
                otp: values.otp,
                otp_id: lastOtpId,
              }).unwrap();

              dispatch(authSlice.actions.login(sid));

              try {
                await updateAgreements({
                  personal_data_agreement: true,
                  user_agreement: true,
                }).unwrap();
              } catch (error) {
                console.error('Failed to update agreements:', error);
              }
            } catch (err) {
              handleFormError(err, formik);
            }
          }}
        >
          {({ values, setFieldValue }) => {
            return (
              <Form>
                <Flex
                  alignItems="flex-start"
                  flexDirection={{ base: 'column', md: 'row' }}
                  justifyContent="space-between"
                >
                  <Box flex="1" w={{ base: 'full', md: 'unset' }}>
                    <FormNumberInput
                      name="otp"
                      value={values.otp}
                      onChange={(val) => {
                        if (val.length > 6) {
                          val = val.substring(0, 6);
                        }

                        setFieldValue('otp', val);
                      }}
                      placeholder={t('smsCode') ?? ''}
                      isDisabled={otpVerifying}
                    />
                  </Box>
                  <Button
                    w={{ base: 'full', md: 'unset' }}
                    ml={{ base: '0', md: '20px' }}
                    mt={{ base: '20px', md: '0' }}
                    type="submit"
                    isLoading={otpVerifying}
                  >
                    {t('continuePhrase')}
                  </Button>
                </Flex>
              </Form>
            );
          }}
        </Formik>
      </Box>
      <Center>
        <Flex direction="column" alignItems="center" w="full">
          {!showCaptcha && phoneNumber && (
            <Countdown
              key={sendNewCodeDate.getTime()}
              date={sendNewCodeDate}
              renderer={({ seconds, completed }) => {
                if (completed) {
                  return (
                    <Button
                      disabled={newCodeRequesting}
                      variant={ButtonVariant.Link}
                      onClick={() => onSendNewCode()}
                    >
                      {t('requestCodeAgain')}
                    </Button>
                  );
                } else {
                  return (
                    <Text color={Color.Shade500}>
                      {t('requestCodeIn', { time: seconds })}
                    </Text>
                  );
                }
              }}
            />
          )}
          {showCaptcha && (
            <Box alignSelf="flex-start">
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY ?? ''}
                onChange={(token) => {
                  onSendNewCode(token);
                  setShowCaptcha(false);
                }}
              />
            </Box>
          )}
          <Button mt="10px" variant={ButtonVariant.Link} onClick={goBack}>
            {t('back')}
          </Button>
        </Flex>
      </Center>
    </LoginTemplate>
  );
};

export default LoginConfirmPhoneContainer;
